import React from "react";
import PropTypes from "prop-types";
import { Tooltip } from "@mqd/volt-base";
import { Icon } from "@mqd/volt-core/src";
import {
  classes,
  isType,
  useOnClickOutside,
  useVoltState
} from "@mqd/volt-toolbox/src";
import css from "./content-switcher.module.css";
export function ContentSwitcher({
  children,
  config,
  activeContent: externalContent,
  onChange
}) {
  const leftNav = useVoltState((state) => state.leftNav);
  const setLeftNav = useVoltState((state) => state.setLeftNav);
  const go = useVoltState((state) => state.go);
  const [isActive, setIsActive] = React.useState(false);
  const [activeContent, setActiveContent] = React.useState(
    () => externalContent || Object.values(config)[0]
  );
  const menuItems = React.useMemo(() => Object.values(config), [config]);
  const wrapRef = React.useRef(null);
  const menuRef = React.useRef(null);
  const handleClickOutside = () => setIsActive(false);
  const handleToggle = () => setIsActive(!isActive);
  const handleContentClick = (config2) => {
    setActiveContent(config2);
    setIsActive(false);
    const hasLeftNav = Boolean(config2.leftNav);
    if (leftNav.show !== hasLeftNav) {
      setLeftNav({ show: hasLeftNav });
    }
    let goPath = config2.path || config2.path;
    if (!goPath && hasLeftNav) {
      let firstLink = config2.leftNav[0];
      if (!firstLink.path && !firstLink.path)
        firstLink = firstLink.children[0];
      goPath = firstLink.path || firstLink.path;
    }
    go(goPath);
    if (isType(onChange, "function")) {
      onChange(config2);
    }
    document.querySelector("#volt-cfn-control").focus();
  };
  useOnClickOutside(wrapRef, handleClickOutside);
  React.useEffect(() => {
    if (!externalContent)
      return;
    if (externalContent.title !== activeContent.title) {
      setActiveContent(externalContent);
    }
  }, [externalContent]);
  React.useEffect(() => {
    if (!menuRef || !menuRef.current)
      return;
    function handleFocusIn(event) {
      menuRef.current.removeEventListener("focusout", handleFocusOut);
      menuRef.current.addEventListener("focusout", handleFocusOut);
    }
    function handleFocusOut(event) {
      setIsActive(false);
    }
    menuRef.current.addEventListener("focusin", handleFocusIn);
    return () => {
      menuRef.current.removeEventListener("focusin", handleFocusIn);
      menuRef.current.removeEventListener("focusout", handleFocusOut);
    };
  }, [menuRef]);
  return /* @__PURE__ */ React.createElement(
    "div",
    {
      className: classes(css.wrap, {
        [css.active]: isActive
      }),
      ref: wrapRef
    },
    /* @__PURE__ */ React.createElement("h1", { className: css.heading }, /* @__PURE__ */ React.createElement(
      Tooltip,
      {
        content: "Switch content",
        direction: "bottomRight",
        disabled: isActive
      },
      /* @__PURE__ */ React.createElement(
        "button",
        {
          id: "volt-cfn-control",
          type: "button",
          className: css.control,
          onClick: handleToggle
        },
        /* @__PURE__ */ React.createElement(Icon, { name: "hamburger", className: css.icon }),
        activeContent.title
      )
    )),
    /* @__PURE__ */ React.createElement("div", { className: css.menu, ref: menuRef }, menuItems.map((item, i) => {
      const { path, title } = item;
      const Component = path ? "a" : "button";
      const itemIsActive = title === activeContent.title;
      const handleOnClick = (event) => {
        event.preventDefault();
        handleContentClick(item);
      };
      return /* @__PURE__ */ React.createElement(
        Component,
        {
          key: path || title,
          className: classes(css.link, { [css.active]: itemIsActive }),
          href: path,
          type: path ? null : "button",
          onClick: handleOnClick,
          tabIndex: itemIsActive ? -1 : 0
        },
        title
      );
    }))
  );
}
ContentSwitcher.propTypes = {
  children: PropTypes.node,
  config: PropTypes.object,
  onChange: PropTypes.func,
  activeContent: PropTypes.object
};
ContentSwitcher.defaultProps = {
  links: []
};
